<section class="chat-shell">
  <header class="chat-head">
    <div class="head-avatar" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M20.5 11.8a7.8 7.8 0 0 1-8.3 7.8 8.5 8.5 0 0 1-3.4-.8L4 20l1.2-4.4a7.8 7.8 0 0 1-.9-3.8 8.1 8.1 0 0 1 16.2 0z" />
        <path d="M17.2 3.7v3.8M15.3 5.6h3.8" />
      </svg>
    </div>

    <div class="head-info">
      <span class="head-name">PeerLiv assistant</span>
    </div>

    <button type="button" class="head-new" aria-label="Start a new chat" (click)="startNewChat()">
      <span class="head-new__label">New chat</span>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-6" />
        <path d="M17.5 3.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
      </svg>
    </button>

    <button type="button" class="head-close" aria-label="Close PeerLiv assistant" (click)="close.emit()">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    </button>
  </header>

  <div #messagesEl class="messages" aria-live="polite">
    @for (message of messages; track message.id) {
      <article
        class="message"
        [class.message--user]="message.role === 'user'"
        [class.message--crisis]="message.crisis"
        [class.message--welcome]="message.isWelcomeMessage"
      >
        @if (message.role === 'user') {
          <p class="message__text">{{ message.text }}</p>
        } @else if (message.isWelcomeMessage) {
          <div class="welcome-card">
            <span class="welcome-card__eyebrow">PeerLiv assistant</span>
            <h2>How can I help today?</h2>
            <p>{{ message.text }}</p>
          </div>
        } @else {
          @if (message.crisis) {
            <app-crisis-card [isCrisis]="true"></app-crisis-card>
          } @else if (message.text || message.isStreaming) {
            <div class="agent-bubble" [class.agent-bubble--support]="isEmotionalSupportMessage(message)">
              @if (message.isStreaming && !message.text.trim()) {
                <div class="typing-wrap" aria-live="polite">
                  <div class="typing" aria-label="Assistant is typing">
                    <span></span><span></span><span></span>
                  </div>
                  <p class="typing-label">{{ loadingLabel }}</p>
                </div>
              } @else if (hasVisibleAssistantText(message)) {
                <p class="message__text" [class.message__text--support]="isEmotionalSupportMessage(message)">
                  {{ visibleAssistantText(message) }}
                </p>
              }

              @switch (message.structured?.kind) {
                @case ('search_posts') {
                  <div class="structured-block">
                    @for (post of $any(message.structured).posts ?? []; track post.postId) {
                      <app-post-result-card [post]="post"></app-post-result-card>
                    }
                  </div>
                }
                @case ('community') {
                  <div class="structured-block">
                    <app-community-join-card
                      [community]="$any(message.structured).community"
                      [starterPosts]="$any(message.structured).starterPosts"
                      (join)="joinCommunityFor(message.id)"
                    ></app-community-join-card>
                  </div>
                }
                @case ('peer_finder') {
                  <div class="structured-block">
                    @for (peer of $any(message.structured).peers ?? []; track peer.handle) {
                      <app-peer-card [peer]="peer"></app-peer-card>
                    }
                  </div>
                }
                @case ('rewrite_post') {
                  <blockquote class="draft-block">
                    <strong>{{ $any(message.structured).draft.title }}</strong>
                    <p>{{ $any(message.structured).draft.body }}</p>
                    @if ($any(message.structured).draft.safetyNotes.length) {
                      <ul>
                        @for (note of $any(message.structured).draft.safetyNotes; track note) {
                          <li>{{ note }}</li>
                        }
                      </ul>
                    }
                  </blockquote>
                }
              }
            </div>
          }
        }

        @if (message.role === 'assistant' && message.traceId && message.responseComplete && !message.isStreaming && !message.crisis) {
          @if (shouldShowContextualActions(message)) {
            <div class="action-row" aria-label="Suggested next actions">
              <span class="action-row__label">Next steps</span>
              @for (action of contextualActions; track action.label) {
                <button
                  type="button"
                  class="action-chip"
                  [class.action-chip--primary]="action.kind === 'use_draft' || action.kind === 'navigate'"
                  (click)="executeAction(action, message.id)"
                >
                  {{ action.label }}
                </button>
              }
            </div>
          }

          @if (message.feedbackSubmitted) {
            <p class="fb-thanks">Thanks for the feedback</p>
          } @else {
            <div class="feedback-row">
              <button type="button" class="fb-btn" (click)="copyAssistantText(message)">
                <lucide-icon name="copy" [size]="14"></lucide-icon>
                {{ copiedMessageId === message.id ? 'Copied' : 'Copy' }}
              </button>
              <button
                type="button"
                class="fb-btn"
                [class.active]="message.feedbackSent === 'helpful'"
                [disabled]="message.feedbackPending"
                (click)="markHelpful(message.id)"
              >
                <lucide-icon name="thumbs-up" [size]="14"></lucide-icon>
                Helpful
              </button>
              <button
                type="button"
                class="fb-btn fb-btn--negative"
                [class.active]="message.feedbackSent === 'not_helpful'"
                [disabled]="message.feedbackPending"
                (click)="markNotHelpful(message.id)"
              >
                <lucide-icon name="thumbs-down" [size]="14"></lucide-icon>
                Not helpful
              </button>
            </div>
          }
          @if (message.feedbackError) {
            <p class="feedback-error">{{ message.feedbackError }}</p>
          }
        }
      </article>
    }
  </div>

  @if (errorMessage) {
    <div
      class="error-banner"
      [class.error-banner--info]="noticeTone === 'info'"
      [class.error-banner--warning]="noticeTone === 'warning'"
      role="alert"
    >
      <span>{{ errorMessage }}</span>
      <button type="button" class="error-banner__retry" (click)="retryLastMessage()">Retry</button>
    </div>
  }

  @if (showIdleNudge) {
    <div class="idle-nudge" aria-live="polite">
      <span>Not sure where to start?</span>
      <button type="button" (click)="submitWithChip(chips[0])">Find similar posts</button>
    </div>
  }

  @if (shouldShowExpandedSuggestions()) {
    <div class="chips-panel" aria-label="Quick actions">
      <div class="chips">
        @for (chip of chips; track chip.label) {
          <button type="button" class="chip" (click)="submitWithChip(chip)">{{ chip.label }}</button>
        }
      </div>
    </div>
  }

  @if (guidedMode) {
    <div class="guided-mode-row">
      <div class="guided-mode-badge" aria-live="polite">
        <span class="guided-mode-badge__label">{{ guidedModeLabel() }}</span>
        <button
          type="button"
          class="guided-mode-badge__dismiss"
          aria-label="Clear guided mode"
          (click)="dismissGuidedMode()"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
    </div>
  }

  <div class="input-row">
    <textarea
      #inputEl
      [(ngModel)]="draftValue"
      (ngModelChange)="handleInput($event)"
      (keydown.enter)="onEnter($event)"
      [disabled]="isStreaming || !assistantAvailable"
      [placeholder]="assistantAvailable ? inputPlaceholder : 'Health Navigator is unavailable right now'"
      rows="1"
      maxlength="4000"
      class="chat-input"
    ></textarea>
    <button
      type="button"
      class="send-btn"
      (click)="submit()"
      [disabled]="!draftValue.trim() || isStreaming || !assistantAvailable"
      aria-label="Send message"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 12h13M13 6l6 6-6 6" />
      </svg>
    </button>
  </div>

  @if (assistantAvailable && inputHelperText) {
    <p class="input-helper">{{ inputHelperText }}</p>
  }

</section>
